/**
 * DspMatrixFilterMode Component
 *
 * DSP-specific filter mode UI. Sibling to MatrixFilterMode — shares child UI
 * components (editors, modals, templates) but owns its own classification,
 * export, and state management.
 *
 * Key differences from MatrixFilterMode:
 * - Uses `useDspClassification` instead of `useMatrixClassification`
 * - Uses DSP-specific export utilities (no hexagonInfo dependency)
 * - Only Matrix (2D) + Simple (1D) modes — no Custom tab
 * - No hexagon size input
 * - Prop is `features` (not `hexagons`)
 *
 * @param {Object} props
 * @param {Array} props.features - Enriched DSP features
 * @param {Function} props.setFilterBoxes - Callback to update parent's filter boxes state
 * @param {boolean} props.isDemo - Whether in demo mode (disables saving)
 * @param {React.Ref} ref - Imperative handle exposing { openExportModal }
 */

import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { ButtonGroup } from 'react-bootstrap';

import {
	createEmptyMatrixFilter,
	createSimpleFilter,
	DEFAULT_PRESET,
	DEFAULT_SIMPLE_PRESET,
} from '../hexagonColorFilter/matrixFilter/matrixClassification';
import useDspClassification from './useDspClassification';
import usePropertyRanges from '../hexagonColorFilter/matrixFilter/usePropertyRanges';
import useAvailableProperties from '../hexagonColorFilter/matrixFilter/useAvailableProperties';
import useCurrencies from '../hexagonColorFilter/matrixFilter/useCurrencies';
import useMatrixTemplates from '../hexagonColorFilter/matrixFilter/useMatrixTemplates';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';
import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import {
	Section,
	SectionTitle,
	StatsTable,
	ColorDot,
} from '../hexagonColorFilter/matrixFilter/StyledMatrixFilterComponents';
import {
	captureMapCanvas,
	captureMapCanvasAsBuffer,
} from '../hexagonColorFilter/matrixFilter/utils';
import { prepareDspExportData, generateDspPDFContent, exportDspToDOCX } from './dspExportUtils';
import ExportReportModal from '../hexagonColorFilter/matrixFilter/ExportReportModal';
import SaveTemplateModal from '../hexagonColorFilter/matrixFilter/SaveTemplateModal';
import SimpleEditor from '../hexagonColorFilter/matrixFilter/SimpleEditor';
import MatrixEditor from '../hexagonColorFilter/matrixFilter/MatrixEditor';

export default forwardRef(function DspMatrixFilterMode({ features, setFilterBoxes, layer, isDemo }, ref) {
	const [filter, setFilter] = useState(() => JSON.parse(JSON.stringify(DEFAULT_PRESET)));
	const [showEditor, setShowEditor] = useState(false);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [showExportModal, setShowExportModal] = useState(false);
	const [activeTemplate, setActiveTemplate] = useState(null);
	const [selectedPreset, setSelectedPreset] = useState('');
	const [isExporting, setIsExporting] = useState(false);
	const [projectNotes, setProjectNotes] = useState('');
	const [exportTheme, setExportTheme] = useState('dark');

	const { addToast } = useToast();
	const { confirm } = useConfirm();
	const { project, pickedTask, mapObject } = useProject();
	const { currentUser } = useAuth();

	// Template management — pass hexagonSize=0 for DSP (irrelevant for DSP templates)
	const { matrixTemplates, saveMutation, saveTemplate, deleteMutation, loadTemplate } =
		useMatrixTemplates({
			activeTemplate,
			setActiveTemplate,
			setFilter,
			setShowSaveModal,
			hexagonSize: 0,
			filter,
		});

	// Get current currency
	const currencies = useCurrencies();
	const currency = currencies.find(c => c.id === filter.currency) || currencies[0];

	// Generate PDF report
	const exportPDF = async (theme = 'dark') => {
		setIsExporting(true);
		try {
			const mapImageData = captureMapCanvas(mapObject);
			const exportData = prepareDspExportData({
				filter,
				classificationResults,
				project,
				currentUser,
				pickedTask,
				dspFeatures: features,
				projectNotes,
				currency,
			});

			const htmlContent = generateDspPDFContent(exportData, theme, mapImageData);

			const printWindow = window.open('', '_blank');
			if (!printWindow) {
				addToast({ title: 'Please allow popups for this site', bg: 'warning' });
				return;
			}

			printWindow.document.write(htmlContent);
			printWindow.document.close();

			addToast({ title: 'Report opened in new tab', bg: 'success' });
		} catch (err) {
			console.error('PDF export error:', err);
			addToast({ title: 'Failed to generate report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	// Generate DOCX report
	const exportDOCX = async () => {
		setIsExporting(true);
		try {
			const mapImageBuffer = captureMapCanvasAsBuffer(mapObject);
			const exportData = prepareDspExportData({
				filter,
				classificationResults,
				project,
				currentUser,
				pickedTask,
				dspFeatures: features,
				projectNotes,
				currency,
			});

			await exportDspToDOCX(exportData, mapImageBuffer);

			addToast({ title: 'DOCX report downloaded', bg: 'success' });
		} catch (err) {
			console.error('DOCX export error:', err);
			addToast({ title: 'Failed to generate DOCX report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	// Handle export with selected options
	const handleExport = format => {
		setShowExportModal(false);
		if (format === 'pdf') {
			exportPDF(exportTheme);
		} else if (format === 'docx') {
			exportDOCX();
		}
	};

	// Stable callback for opening export modal
	const openExportModal = useCallback(() => setShowExportModal(true), []);

	// Expose export trigger to parent via imperative handle
	useImperativeHandle(
		ref,
		() => ({
			openExportModal,
		}),
		[openExportModal]
	);

	// Property ranges for editor display
	const propertyRange = usePropertyRanges(features, filter.property);
	const bandRange = usePropertyRanges(features, filter.bandProperty);
	const thresholdRange = usePropertyRanges(features, filter.thresholdProperty);

	// Classification logic (DSP-specific)
	const classificationResults = useDspClassification({
		features,
		filter,
		setFilterBoxes,
		layer,
	});

	// Stable callback for updating filter
	const updateFilter = useCallback(updates => {
		setFilter(prev => ({ ...prev, ...updates }));
	}, []);

	// Derive available properties from actual DSP feature data
	const availableProperties = useAvailableProperties(features);

	const bandPropertyLabel =
		availableProperties.find(p => p.id === filter.bandProperty)?.label || filter.bandProperty;
	const thresholdPropertyLabel =
		availableProperties.find(p => p.id === filter.thresholdProperty)?.label ||
		filter.thresholdProperty;
	const isSimpleMode = filter.type === 'simple';

	// Pre-filter templates by current mode to avoid double iteration in JSX
	const filteredTemplates = matrixTemplates.filter(t =>
		isSimpleMode ? t.matrixConfig?.type === 'simple' : t.matrixConfig?.type === 'matrix'
	);

	return (
		<div>
			{/* Filter Mode Toggle */}
			<Section>
				<SectionTitle>Filter mode</SectionTitle>
				<ButtonGroup className="w-100 mb-2">
					<Button
						variant={!isSimpleMode ? 'primary' : 'outline-secondary'}
						size="sm"
						onClick={() => {
							if (isSimpleMode) {
								setFilter(JSON.parse(JSON.stringify(DEFAULT_PRESET)));
								setActiveTemplate(null);
								setSelectedPreset('');
								setProjectNotes('');
							}
						}}>
						Matrix (2D)
					</Button>
					<Button
						variant={isSimpleMode ? 'primary' : 'outline-secondary'}
						size="sm"
						onClick={() => {
							if (!isSimpleMode) {
								setFilter(JSON.parse(JSON.stringify(DEFAULT_SIMPLE_PRESET)));
								setActiveTemplate(null);
								setSelectedPreset('');
								setProjectNotes('');
							}
						}}>
						Simple (1D)
					</Button>
				</ButtonGroup>
			</Section>

			{/* Template Selection */}
			<Section>
				<SectionTitle>Filter template</SectionTitle>
				<Form.Select
					size="sm"
					value={activeTemplate?.uuid || selectedPreset}
					onChange={e => {
						const value = e.target.value;
						if (value === 'default') {
							setFilter(JSON.parse(JSON.stringify(DEFAULT_PRESET)));
							setActiveTemplate(null);
							setSelectedPreset('default');
							setProjectNotes('');
						} else if (value === 'simple_default') {
							setFilter(JSON.parse(JSON.stringify(DEFAULT_SIMPLE_PRESET)));
							setActiveTemplate(null);
							setSelectedPreset('simple_default');
							setProjectNotes('');
						} else if (value === 'empty') {
							setFilter(createEmptyMatrixFilter());
							setActiveTemplate(null);
							setSelectedPreset('empty');
							setProjectNotes('');
						} else if (value === 'simple_empty') {
							setFilter(createSimpleFilter());
							setActiveTemplate(null);
							setSelectedPreset('simple_empty');
							setProjectNotes('');
						} else if (value) {
							const template = matrixTemplates.find(t => t.uuid === value);
							if (template) loadTemplate(template);
							setSelectedPreset('');
							setProjectNotes('');
						} else {
							setSelectedPreset('');
						}
					}}>
					<option value="">-- Select template --</option>
					{!isSimpleMode && (
						<optgroup label="Matrix Presets (2D)">
							<option value="default">Default Matrix (5 Classes)</option>
							<option value="empty">Empty Matrix</option>
						</optgroup>
					)}
					{isSimpleMode && (
						<optgroup label="Simple Presets (1D)">
							<option value="simple_default">Volume Classification</option>
							<option value="simple_empty">Empty Simple Filter</option>
						</optgroup>
					)}
					{filteredTemplates.length > 0 && (
						<optgroup label="Saved templates">
							{filteredTemplates.map(t => (
								<option key={t.uuid} value={t.uuid}>
									{t.title}
								</option>
							))}
						</optgroup>
					)}
				</Form.Select>

				{/* Save/Delete buttons */}
				<div className="d-flex gap-2 mt-2">
					<Button
						variant="outline-success"
						size="sm"
						className="flex-fill"
						onClick={() => setShowSaveModal(true)}
						disabled={isDemo}>
						{activeTemplate ? '💾 Update template' : '💾 Save as template'}
					</Button>
					{activeTemplate && (
						<Button
							variant="outline-danger"
							size="sm"
							onClick={async () => {
								const userConfirmed = await confirm({
									title: 'Delete Filter Template',
									message: (
										<>
											Are you sure you want to delete the filter template{' '}
											<strong>{activeTemplate.title}</strong>?
										</>
									),
								});
								if (userConfirmed) {
									deleteMutation.mutate(activeTemplate.uuid);
								}
							}}
							disabled={isDemo}>
							🗑️
						</Button>
					)}
				</div>
			</Section>

			{/* Results Summary */}
			<Section>
				<SectionTitle>Classification Results</SectionTitle>
				<Card>
					<Card.Body className="p-0">
						<StatsTable striped hover size="sm" className="mb-0">
							<thead>
								<tr>
									<th>Class</th>
									<th>Count</th>
									<th>Area</th>
									<th>%</th>
								</tr>
							</thead>
							<tbody>
								{filter.classes.map(cls => {
									const r = classificationResults[cls.id];
									return (
										<tr key={cls.id}>
											<td>
												<ColorDot color={cls.color} />
												{cls.label}
											</td>
											<td>{r?.count || 0}</td>
											<td>{(r?.totalArea || 0).toLocaleString()} m²</td>
											<td>{r?.areaPercentage || 0}%</td>
										</tr>
									);
								})}
								{classificationResults.below_minimum?.count > 0 && (
									<tr className="text-muted">
										<td>
											<ColorDot color="#808080" />
											Below Minimum
										</td>
										<td>{classificationResults.below_minimum.count}</td>
										<td>
											{classificationResults.below_minimum.totalArea.toLocaleString()}{' '}
											m²
										</td>
										<td>
											{classificationResults.below_minimum.areaPercentage}%
										</td>
									</tr>
								)}
								{classificationResults.unclassified?.count > 0 && (
									<tr className="text-muted">
										<td>
											<ColorDot color="#646464" />
											Unclassified
										</td>
										<td>{classificationResults.unclassified.count}</td>
										<td>
											{classificationResults.unclassified.totalArea.toLocaleString()}{' '}
											m²
										</td>
										<td>
											{classificationResults.unclassified.areaPercentage}%
										</td>
									</tr>
								)}
							</tbody>
						</StatsTable>
					</Card.Body>
				</Card>
			</Section>

			{/* Toggle Editor */}
			<Button
				variant="outline-secondary"
				size="sm"
				className="w-100 mb-3"
				onClick={() => setShowEditor(true)}>
				⚙️ Edit filter
			</Button>

			{/* Editor Offcanvas (Bottom Drawer) */}
			<Offcanvas
				show={showEditor}
				onHide={() => setShowEditor(false)}
				placement="bottom"
				style={{ height: 'auto', maxHeight: '50vh' }}
				backdrop={false}
				scroll={true}>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title>
						{isSimpleMode ? 'Simple Filter Editor' : 'Matrix Filter Editor'}
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					{isSimpleMode ? (
						<SimpleEditor
							filter={filter}
							updateFilter={updateFilter}
							classificationResults={classificationResults}
							currency={currency}
							currencies={currencies}
							propertyRange={propertyRange}
							availableProperties={availableProperties}
							projectNotes={projectNotes}
							setProjectNotes={setProjectNotes}
							isExporting={isExporting}
							onOpenExportModal={openExportModal}
						/>
					) : (
						<MatrixEditor
							filter={filter}
							updateFilter={updateFilter}
							classificationResults={classificationResults}
							currency={currency}
							currencies={currencies}
							bandPropertyLabel={bandPropertyLabel}
							thresholdPropertyLabel={thresholdPropertyLabel}
							availableProperties={availableProperties}
							bandRange={bandRange}
							thresholdRange={thresholdRange}
							projectNotes={projectNotes}
							setProjectNotes={setProjectNotes}
							isExporting={isExporting}
							onOpenExportModal={openExportModal}
						/>
					)}
				</Offcanvas.Body>
			</Offcanvas>

			{/* Export Options Modal */}
			<ExportReportModal
				show={showExportModal}
				onHide={() => setShowExportModal(false)}
				exportTheme={exportTheme}
				setExportTheme={setExportTheme}
				onExport={handleExport}
				isExporting={isExporting}
			/>

			{/* Save Template Modal */}
			<SaveTemplateModal
				show={showSaveModal}
				onHide={() => setShowSaveModal(false)}
				activeTemplate={activeTemplate}
				filterName={filter.name}
				onSave={saveTemplate}
				isPending={saveMutation.isPending}
			/>
		</div>
	);
});
